import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Users, FileText, DollarSign, Trophy, Plus, Boxes, Clock, AlertTriangle, Send, TrendingUp, Bell, Check, Medal } from "lucide-react";
import { UserAvatar } from "../components/UserAvatar";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbRange, setLbRange] = useState("all");

  const loadFollowUps = () => {
    api.get("/admin/follow-ups").then(({ data }) => setFollowUps(data)).catch(() => {});
  };
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    loadFollowUps();
  }, []);
  useEffect(() => {
    api.get(`/admin/sales-leaderboard?range=${lbRange}`).then(({ data }) => setLeaderboard(data)).catch(() => {});
  }, [lbRange]);

  const completeFollowUp = async (f, e) => {
    e.stopPropagation();
    try {
      await api.post(`/admin/clients/${encodeURIComponent(f.client_email)}/notes/${f.id}/done`);
      setFollowUps((list) => list.filter((x) => x.id !== f.id));
    } catch {}
  };

  const statCards = [
    { label: "Total Quotes", value: stats?.total_quotes ?? "—", icon: FileText, bg: "bg-blue-50", border: "border-blue-100", iconColor: "text-blue-600", onClick: () => navigate("/admin/quotes"), testid: "stat-quotes" },
    { label: "Customers", value: stats?.customers ?? "—", icon: Users, bg: "bg-amber-50", border: "border-amber-100", iconColor: "text-amber-700", onClick: () => navigate("/admin/clients"), testid: "stat-clients" },
    { label: "Pipeline", value: fmt(stats?.pipeline_value), icon: DollarSign, bg: "bg-green-50", border: "border-green-100", iconColor: "text-green-600", sub: stats ? `Proj. profit ${fmt(stats.pipeline_profit)}` : null },
    { label: "Won", value: fmt(stats?.won_value), icon: Trophy, bg: "bg-red-50", border: "border-red-100", iconColor: "text-red-600", sub: stats ? `Profit ${fmt(stats.won_profit)} · ${stats.won_margin_pct}%` : null },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Admin Console</span>
          <h1 className="mt-2 font-display font-black text-4xl tracking-tighter">Overview</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => navigate("/admin/quotes")} data-testid="nav-quotes-btn" variant="outline" className="rounded-none border-zinc-900 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:text-purple-800 font-mono uppercase tracking-widest">
            <FileText className="h-4 w-4 mr-1" /> Quotes
          </Button>
          <Button onClick={() => navigate("/admin/clients")} data-testid="nav-clients-btn" variant="outline" className="rounded-none border-zinc-900 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:text-purple-800 font-mono uppercase tracking-widest">
            <Users className="h-4 w-4 mr-1" /> Clients
          </Button>
          <Button onClick={() => navigate("/admin/users")} data-testid="nav-users-btn" variant="outline" className="rounded-none border-zinc-900 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:text-purple-800 font-mono uppercase tracking-widest">
            <Users className="h-4 w-4 mr-1" /> Users
          </Button>
          <Button onClick={() => navigate("/admin/onboarding")} data-testid="onboarding-nav-btn" variant="outline" className="rounded-none border-zinc-900 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:text-purple-800 font-mono uppercase tracking-widest">
            <FileText className="h-4 w-4 mr-1" /> Onboarding
          </Button>
          <Button onClick={() => navigate("/admin/catalog")} data-testid="manage-catalog-btn" variant="outline" className="rounded-none border-zinc-900 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:text-purple-800 font-mono uppercase tracking-widest">
            <Boxes className="h-4 w-4 mr-1" /> Manage Catalog
          </Button>
          <Button onClick={() => navigate("/build")} data-testid="admin-add-quote-btn" className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest">
            <Plus className="h-4 w-4 mr-1" /> Add Quote
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statCards.map((s) => {
          const Tag = s.onClick ? "button" : "div";
          return (
            <Tag
              key={s.label}
              onClick={s.onClick}
              data-testid={s.testid}
              className={`${s.bg} border ${s.border} p-6 text-left ${s.onClick ? "transition-shadow hover:shadow-md cursor-pointer" : ""}`}
            >
              <s.icon className={`h-5 w-5 ${s.iconColor}`} />
              <p className="mt-3 font-mono text-xs uppercase tracking-widest text-zinc-500">{s.label}</p>
              <p className="mt-1 font-display font-black text-2xl lg:text-3xl tracking-tighter">{s.value}</p>
              {s.sub && <p data-testid={`stat-sub-${s.label.toLowerCase()}`} className="mt-1 font-mono text-[11px] text-green-700">{s.sub}</p>}
            </Tag>
          );
        })}
      </div>

      <div className="border border-zinc-200 bg-white p-5" data-testid="pipeline-health">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-4 w-4 text-primary" />
          <h2 className="font-mono text-xs uppercase tracking-widest text-zinc-600">Pipeline Health</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <button onClick={() => navigate("/admin/quotes?filter=expiring")} data-testid="ph-expiring-btn" className="flex items-center gap-3 p-2 -m-2 text-left transition-colors hover:bg-zinc-50">
            <div className="h-9 w-9 bg-amber-50 border border-amber-100 flex items-center justify-center shrink-0">
              <Clock className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <p data-testid="ph-expiring" className="font-display font-black text-2xl tracking-tighter leading-none">{stats?.expiring_soon ?? "—"}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Expiring ≤ 3 days</p>
            </div>
          </button>
          <button onClick={() => navigate("/admin/quotes?filter=expired")} data-testid="ph-expired-btn" className="flex items-center gap-3 p-2 -m-2 text-left transition-colors hover:bg-zinc-50">
            <div className="h-9 w-9 bg-red-50 border border-red-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <p data-testid="ph-expired" className="font-display font-black text-2xl tracking-tighter leading-none">{stats?.expired_active ?? "—"}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Expired (open)</p>
            </div>
          </button>
          <div className="flex items-center gap-3 p-2 -m-2">
            <div className="h-9 w-9 bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
              <Send className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p data-testid="ph-reminders" className="font-display font-black text-2xl tracking-tighter leading-none">{stats?.reminders_sent ?? "—"}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Reminders sent</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-2 -m-2" title="Won revenue from website-audit upsell quotes">
            <div className="h-9 w-9 bg-green-50 border border-green-100 flex items-center justify-center shrink-0">
              <TrendingUp className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p data-testid="ph-audit-revenue" className="font-display font-black text-2xl tracking-tighter leading-none">${(stats?.audit_won_value ?? 0).toLocaleString()}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Audit won · {stats?.audit_count ?? 0} quotes</p>
            </div>
          </div>
          <button onClick={() => navigate("/admin/onboarding")} data-testid="ph-onboarding-btn" className="flex items-center gap-3 p-2 -m-2 text-left transition-colors hover:bg-zinc-50" title="Clients who haven't finished onboarding">
            <div className="h-9 w-9 bg-purple-50 border border-purple-100 flex items-center justify-center shrink-0">
              <FileText className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p data-testid="ph-onboarding" className="font-display font-black text-2xl tracking-tighter leading-none">{stats?.onboarding_pending ?? "—"}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Onboarding pending</p>
            </div>
          </button>
        </div>
      </div>

      <div className="border border-zinc-200 bg-white p-5 mt-6" data-testid="sales-leaderboard-panel">
        <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-primary" />
            <h2 className="font-mono text-xs uppercase tracking-widest text-zinc-600">Sales Leaderboard</h2>
          </div>
          <div className="flex border border-zinc-200">
            {[{ k: "month", l: "This Month" }, { k: "quarter", l: "Quarter" }, { k: "all", l: "All-Time" }].map((r) => (
              <button
                key={r.k}
                data-testid={`lb-range-${r.k}`}
                onClick={() => setLbRange(r.k)}
                className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${lbRange === r.k ? "bg-primary text-white" : "bg-white text-zinc-500 hover:text-zinc-900"}`}
              >
                {r.l}
              </button>
            ))}
          </div>
        </div>
        {leaderboard.length === 0 ? (
          <p className="text-sm text-zinc-400 font-mono" data-testid="leaderboard-empty">No salespeople yet. Invite your team from the Users page.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="text-left px-2 py-2 w-8">#</th>
                  <th className="text-left px-2 py-2">Salesperson</th>
                  <th className="text-right px-2 py-2">Quotes</th>
                  <th className="text-right px-2 py-2">Won</th>
                  <th className="text-right px-2 py-2">Win rate</th>
                  <th className="text-right px-2 py-2">Pipeline</th>
                  <th className="text-right px-2 py-2">Revenue won</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((sp, i) => {
                  const medal = i === 0 ? "text-amber-500" : i === 1 ? "text-zinc-400" : i === 2 ? "text-amber-700" : null;
                  return (
                    <tr key={sp.id} data-testid={`leaderboard-row-${sp.id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-2 py-3">
                        {medal ? <Medal className={`h-4 w-4 ${medal}`} /> : <span className="font-mono text-xs text-zinc-400">{i + 1}</span>}
                      </td>
                      <td className="px-2 py-3">
                        <div className="flex items-center gap-2.5">
                          <UserAvatar name={sp.name} email={sp.email} src={sp.avatar_url} className="h-8 w-8" />
                          <div className="min-w-0">
                            <p className="font-medium truncate">{sp.name}</p>
                            <p className="text-xs text-zinc-500 truncate">{sp.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-3 text-right font-mono">{sp.quotes}</td>
                      <td className="px-2 py-3 text-right font-mono">{sp.won}</td>
                      <td className="px-2 py-3 text-right font-mono">{sp.win_rate}%</td>
                      <td className="px-2 py-3 text-right font-mono text-zinc-500">{fmt(sp.pipeline)}</td>
                      <td className="px-2 py-3 text-right font-mono font-bold text-green-700" data-testid={`leaderboard-revenue-${sp.id}`}>{fmt(sp.revenue_won)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="border border-zinc-200 bg-white p-5 mt-6" data-testid="follow-ups-panel">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="h-4 w-4 text-primary" />
          <h2 className="font-mono text-xs uppercase tracking-widest text-zinc-600">Follow-ups</h2>
          {followUps.length > 0 && (
            <span data-testid="follow-ups-count" className="font-mono text-[10px] bg-primary/10 text-primary px-2 py-0.5">{followUps.length}</span>
          )}
        </div>
        {followUps.length === 0 ? (
          <p className="text-sm text-zinc-400 font-mono" data-testid="follow-ups-empty">No follow-ups scheduled. Add a reminder from a client's notes.</p>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {followUps.map((f) => (
              <li
                key={f.id}
                data-testid={`follow-up-${f.id}`}
                onClick={() => navigate(`/admin/clients/${encodeURIComponent(f.client_email)}`)}
                className="flex items-center gap-3 py-3 cursor-pointer hover:bg-zinc-50 -mx-2 px-2 transition-colors"
              >
                <span className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-0.5 shrink-0 ${f.overdue ? "bg-red-50 text-red-700 border-red-200" : f.due_today ? "bg-amber-50 text-amber-700 border-amber-100" : "bg-zinc-50 text-zinc-500 border-zinc-200"}`}>
                  {f.overdue ? "Overdue" : f.due_today ? "Today" : new Date(f.remind_at + "T00:00:00").toLocaleDateString()}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{f.client_name}</p>
                  <p className="text-xs text-zinc-500 truncate">{f.body}</p>
                </div>
                <button
                  onClick={(e) => completeFollowUp(f, e)}
                  data-testid={`follow-up-done-${f.id}`}
                  className="shrink-0 flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-green-600 hover:text-green-700 border border-green-200 hover:border-green-400 px-2 py-1 transition-colors"
                  title="Mark done"
                >
                  <Check className="h-3 w-3" /> Done
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
